import { useEffect, useState } from 'react';
import { useClassPerfChapterMastery } from '../../../api/hooks';
import { ArrowRight } from 'lucide-react';
import { Button, EmptyState, PanelCard, Segmented, Term } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import { FaClipboardList } from '../../../components/ui/icons';
import ChapterMasteryTable, { type ChapterOrder } from '../components/ChapterMasteryTable';
import RemediateDialog from '../components/RemediateDialog';
import { DEFAULT_SCOPES } from '../../../lib/scopes';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** "Mathematics", for the title. */
  subjLabel?: string;
  /** "Grade 10", for the `· 10B` suffix next to student names. */
  gradeName?: string;
  /** Name ("B") of the section picked in the page filters. */
  filteredSectionName?: string;
  /** Offered by the empty state's "Show ..." button. */
  suggestSubject?: { id: number; name: string };
  onPickSubject?: (id: number) => void;
}

export default function ChaptersTab({ gradeId, sectionId, subjectId, weeks, subjLabel, gradeName, filteredSectionName, suggestSubject, onPickSubject }: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const [order, setOrder] = useState<ChapterOrder>('name');
  const [open, setOpen] = useState(false);
  const q = useClassPerfChapterMastery({
    grade: gradeId, section: sectionId, subject: subjectId, weeks,
  });
  useEffect(() => { setSelected([]); }, [gradeId, subjectId]);
  if (subjectId === 'all') {
    return (
      <PanelCard>
        <EmptyState icon={<FaClipboardList />} title="Pick a subject"
          body="Chapter mastery is shown one subject at a time. Choose one above."
          action={suggestSubject && onPickSubject
            ? <Button variant="ghost" onClick={() => onPickSubject(suggestSubject.id)}>{`Show ${suggestSubject.name}`}</Button>
            : undefined} />
      </PanelCard>
    );
  }
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  if (q.data.students.length === 0) {
    return <PanelCard><EmptyState icon={<FaClipboardList />} title="No students in this cohort" /></PanelCard>;
  }
  const d = q.data;
  const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const cell: Record<string, number> = {};
  d.cells.forEach((c) => { cell[`${c.student_id}-${c.chapter_id}`] = c.pct; });
  const passPct = d.pass_score_pct;
  const selectedNames = d.chapters.filter((c) => selected.includes(c.id)).map((c) => c.name).join(', ');
  // Students under the pass mark on at least one of the ticked chapters (same rule the dialog uses to pre-tick).
  const belowCount = new Set(
    d.cells.filter((c) => selected.includes(c.chapter_id) && c.pct < passPct).map((c) => c.student_id),
  ).size;
  return (
    <>
      <PanelCard title={subjLabel ? `Chapter mastery · ${subjLabel}` : 'Chapter mastery'} help="chapter_mastery" align="end"
        subtitle="Tick chapter headers to pick chapters for a remediation cycle. Cells show % correct per student."
        actions={(
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
              <Term k="weakest_first">Chapter order</Term>
            </span>
            <Segmented<ChapterOrder> label="Chapter order" value={order} onChange={setOrder}
              options={[{ key: 'name', label: 'By name' }, { key: 'weakest', label: 'Weakest first' }]} />
          </div>
        )}>
        <ChapterMasteryTable data={d} selected={selected} onToggleChapter={toggle} order={order}
          gradeName={gradeName} filteredSectionName={filteredSectionName} />
      </PanelCard>
      {selected.length > 0 && (
        <div role="region" aria-label="Selected chapters"
          className="sticky bottom-4 z-10 mt-[18px] flex flex-wrap items-center gap-3 rounded-[16px] bg-navy py-3 pl-[18px] pr-3 text-white shadow-actbar">
          <div className="min-w-0 flex-[1_1_260px]">
            <p className="truncate font-semibold">{selected.length} selected · {selectedNames}</p>
            <p className="text-[13px] text-on-navy-muted">{belowCount} {belowCount === 1 ? 'student is' : 'students are'} below {passPct}% on at least one of them</p>
          </div>
          <Button size="md" variant="ghost-on-navy" className="min-h-[40px] !border-muted-2" onClick={() => setSelected([])}>Clear</Button>
          <Button size="md" className="min-h-[40px]" onClick={() => setOpen(true)}>
            <span className="inline-flex items-center gap-2">Remediate selected ({selected.length})<ArrowRight size={18} strokeWidth={1.8} aria-hidden /></span>
          </Button>
        </div>
      )}
      {open && (
        <RemediateDialog
          key={selected.join(',')}
          isOpen onClose={() => { setOpen(false); }}
          cohort={{
            grade_id: gradeId, section_id: sectionId === 'all' ? null : sectionId,
            subject_id: subjectId, scopes: [...DEFAULT_SCOPES], source_exam_id: null,
          }}
          chapters={d.chapters.filter((c) => selected.includes(c.id)).map((c) => ({ chapter_id: c.id, name: c.name }))}
          candidates={d.students.map((s) => ({
            student_id: s.user_id, name: `${s.first_name} ${s.last_name}`.trim() || s.username,
            pct_by_chapter: Object.fromEntries(d.chapters.map((c) => [c.id, cell[`${s.user_id}-${c.id}`] ?? null])),
          }))}
          passPct={d.pass_score_pct}
        />
      )}
    </>
  );
}
